import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaFileAlt,
  FaDownload,
  FaFolderOpen,
  FaFileContract,
  FaFileInvoiceDollar,
  FaReceipt,
  FaFilePdf,
  FaFileImage,
  FaArchive,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useBuilding } from "../context/BuildingContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { canResidentSeeDoc, DOC_VISIBILITY } from "../utils/docVisibility";
import { esPermisoDenegado } from "../utils/consultasEdificio";
import { normalizeDocType, docTypeKey } from "../utils/docTypes";
import EmptyStateComingSoon from "../components/EmptyStateComingSoon";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppEmptyState,
  AppSpinner,
  Badge,
  SectionLabel,
  IconBox,
} from "../components/app";
import { appColors, appType, appSpacing, appRadius } from "../utils/appTheme";
import { formatDateLocalized } from "../utils/formatters";

// Icono según el tipo/extensión del documento (solo presentación).
const iconForDoc = (tipo, fileName) => {
  const t = (tipo || "").toString().toLowerCase();
  const name = (fileName || "").toString().toLowerCase();
  if (/factura|invoice|comprobante|recibo|gasto/.test(t)) return <FaFileInvoiceDollar />;
  if (/acta|reuni|asamblea/.test(t)) return <FaReceipt />;
  if (/reglamento|contrato|legal|seguro|poliza|póliza/.test(t)) return <FaFileContract />;
  if (name.endsWith(".pdf")) return <FaFilePdf />;
  if (/\.(png|jpe?g|webp|gif|heic)$/.test(name)) return <FaFileImage />;
  if (/\.(zip|rar|7z)$/.test(name)) return <FaArchive />;
  return <FaFileAlt />;
};

/**
 * Documentos del edificio que el residente puede ver, con la visibilidad en la
 * consulta (reglas 0D, C12: la regla mira buildingdocs.visibilidad; el backfill
 * puso "todos" en los docs viejos sin el campo). El propietario vigente pide
 * "todos" y "propietarios"; el resto, "todos". Si la regla no lo considera
 * propietario (p. ej. un user_access que todavía no refleja la unidad), la
 * consulta con "propietarios" se niega entera y se repite solo con "todos".
 * "ninguno" (solo administración) no se pide nunca.
 *
 * @param {string | number} buildingId - Edificio
 * @param {boolean} esPropietario - Propietario vigente en el edificio
 */
export async function leerDocumentosVisibles(buildingId, esPropietario) {
  const consultar = (visibilidades) =>
    getDocs(
      query(
        collection(db, "buildingdocs"),
        where("buildingid", "in", tolerantId(buildingId)),
        where("visibilidad", "in", visibilidades)
      )
    );
  let snap;
  try {
    snap = await consultar(
      esPropietario ? [DOC_VISIBILITY.TODOS, DOC_VISIBILITY.PROPIETARIOS] : [DOC_VISIBILITY.TODOS]
    );
  } catch (e) {
    if (!esPropietario || !esPermisoDenegado(e)) throw e;
    snap = await consultar([DOC_VISIBILITY.TODOS]);
  }
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Fecha de carga del documento. Los docs viejos no tienen createdAt: usan la
// fecha del documento para no quedar todos al final del orden.
const uploadedAtOf = (d) => d.createdAt || d.date || d.fecha || null;
const toMs = (v) => {
  if (!v) return 0;
  if (v.seconds) return v.seconds * 1000;
  if (v.toDate) return v.toDate().getTime();
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const DocumentacionEdificio = ({ setSelected, goBack } = {}) => {
  const { t } = useTranslation("documentacionEdificio");
  const { selectedBuilding, userUnitsInBuilding } = useBuilding();
  const isMobile = useIsMobile();
  const [rawDocs, setRawDocs] = useState([]);
  // Visibilidad elegida por el admin: el inquilino solo ve "todos"; el
  // propietario VIGENTE ve "todos" + "propietarios" (la regla mira ownerBids:
  // el ex-propietario ya no es propietario); "ninguno" no lo ve nadie.
  const isOwner = useMemo(
    () =>
      !selectedBuilding?.isReadOnly &&
      (userUnitsInBuilding || []).some((u) => u.userRole === "Propietario" && !u.isHistorical),
    [userUnitsInBuilding, selectedBuilding?.isReadOnly]
  );
  // Orden por fecha de carga (createdAt); "desc" = más recientes primero.
  const [sortDir, setSortDir] = useState("desc");
  const documentos = useMemo(() => {
    const visibles = rawDocs.filter((d) => canResidentSeeDoc(d, isOwner));
    return visibles.sort((a, b) => {
      const diff = toMs(uploadedAtOf(a)) - toMs(uploadedAtOf(b));
      return sortDir === "desc" ? -diff : diff;
    });
  }, [rawDocs, isOwner, sortDir]);
  const toggleSort = () => setSortDir((d) => (d === "desc" ? "asc" : "desc"));
  // Tipo guardado (normalizado: "Acta"→"Actas de asambleas", "Otro"→"Otros"),
  // su etiqueta traducida y una explicación corta de qué es.
  const tipoDe = (d) => normalizeDocType(d.type || d.tipo);
  const labelDe = (tipo) => t(`typeLabel.${docTypeKey(tipo)}`, { defaultValue: tipo });
  const descDe = (tipo) => t(`typeDesc.${docTypeKey(tipo)}`);
  const [reglamentos, setReglamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // isOwner puede cambiar cuando terminan de cargar las unidades: una carga
    // vieja (con otra visibilidad) no pisa a la nueva.
    let vigente = true;
    const fetchDocumentos = async () => {
      if (!selectedBuilding?.buildingid) {
        setLoading(false);
        return;
      }
      try {
        // Documentos generales (buildingdocs), con la visibilidad en la consulta.
        const visibles = await leerDocumentosVisibles(selectedBuilding.buildingid, isOwner);
        if (!vigente) return;
        setRawDocs(visibles);

        // Reglamentos del edificio (campos en el doc building)
        if (selectedBuilding.id) {
          const bSnap = await getDoc(doc(db, "building", selectedBuilding.id));
          if (vigente && bSnap.exists()) {
            const b = bSnap.data();
            const items = [];
            if (b.reglamentoCopropiedadUrl) {
              items.push({
                id: "reglamento-copropiedad",
                descKey: "copropiedad",
                fileName: b.reglamentoCopropiedadFileName,
                fileUrl: b.reglamentoCopropiedadUrl,
                date: b.reglamentoCopropiedadUploadedAt,
                type: "Reglamento",
              });
            }
            if (b.reglamentoInternoUrl) {
              items.push({
                id: "reglamento-interno",
                descKey: "interno",
                fileName: b.reglamentoInternoFileName,
                fileUrl: b.reglamentoInternoUrl,
                date: b.reglamentoInternoUploadedAt,
                type: "Reglamento",
              });
            }
            setReglamentos(items);
          }
        }
      } catch (error) {
        console.error("Error al cargar documentacion:", error);
      } finally {
        if (vigente) setLoading(false);
      }
    };
    fetchDocumentos();
    return () => {
      vigente = false;
    };
  }, [selectedBuilding, isOwner]);

  const formatDate = (date) => {
    if (!date) return "";
    if (date?.seconds) return formatDateLocalized(new Date(date.seconds * 1000));
    return String(date);
  };

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    const mobileHeader = (
      <AppHeader
        title={t("titleMobile")}
        subtitle={selectedBuilding?.name || t("subtitleMobileFallback")}
        onBack={goBack}
      />
    );

    if (loading) {
      return (
        <AppScreen header={mobileHeader}>
          <AppSpinner />
        </AppScreen>
      );
    }

    const noDataMobile = documentos.length === 0 && reglamentos.length === 0;

    return (
      <AppScreen header={mobileHeader}>
        {noDataMobile ? (
          <AppEmptyState
            icon={<FaFolderOpen />}
            title={t("empty.title")}
            description={t("empty.description")}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: appSpacing.section }}>
            {reglamentos.length > 0 && (
              <div>
                <SectionLabel style={{ marginBottom: 12 }}>{t("sections.rules")}</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {reglamentos.map((r) => (
                    <AppCard
                      key={r.id}
                      accent="blue"
                      onClick={() => window.open(r.fileUrl, "_blank", "noopener,noreferrer")}
                      padding={16}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                        <IconBox icon={<FaFileContract />} size={44} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.blue, marginBottom: 3 }}>
                            {t("ruleLabel")}
                          </div>
                          <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                            {r.descKey ? t(`rules.${r.descKey}`) : r.description}
                          </div>
                          {r.descKey && (
                            <div style={{ ...appType.bodySm, marginTop: 2, overflowWrap: "anywhere" }}>
                              {t(`ruleDesc.${r.descKey}`)}
                            </div>
                          )}
                        </div>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 34,
                            height: 34,
                            borderRadius: appRadius.iconBox,
                            background: appColors.surface2,
                            color: appColors.blue,
                            flexShrink: 0,
                          }}
                        >
                          <FaDownload style={{ fontSize: 13 }} />
                        </span>
                      </div>
                      {r.date && (
                        <div
                          style={{
                            marginTop: 12,
                            paddingTop: 12,
                            borderTop: `1px solid ${appColors.line2}`,
                            minWidth: 0,
                          }}
                        >
                          <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>
                            {t("uploaded")}
                          </div>
                          <div style={{ ...appType.body, fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
                            {formatDate(r.date)}
                          </div>
                        </div>
                      )}
                    </AppCard>
                  ))}
                </div>
              </div>
            )}

            {documentos.length > 0 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
                  <SectionLabel>{t("sections.documents")}</SectionLabel>
                  <button type="button" onClick={toggleSort} aria-label={t("sort.label")} style={styles.sortChip}>
                    {sortDir === "desc" ? <FaSortAmountDown /> : <FaSortAmountUp />}
                    {sortDir === "desc" ? t("sort.newest") : t("sort.oldest")}
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {documentos.map((docItem) => {
                    const fileUrl = docItem.fileUrl || docItem.archivo;
                    const tipo = tipoDe(docItem);
                    const subido = formatDate(uploadedAtOf(docItem));
                    const fileName = docItem.fileName || docItem.nombre;
                    return (
                      <AppCard
                        key={docItem.id}
                        onClick={fileUrl ? () => window.open(fileUrl, "_blank", "noopener,noreferrer") : undefined}
                        padding={16}
                      >
                        {/* Cabecera: icono por tipo + micro-label TIPO + título */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, minWidth: 0 }}>
                          <IconBox icon={iconForDoc(tipo, fileName)} size={44} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>
                              {labelDe(tipo)}
                            </div>
                            <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                              {docItem.description || docItem.descripcion || t("noDescription")}
                            </div>
                            <div style={{ ...appType.bodySm, marginTop: 2, overflowWrap: "anywhere" }}>
                              {descDe(tipo)}
                            </div>
                          </div>
                          {fileUrl ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                borderRadius: appRadius.iconBox,
                                background: appColors.surface2,
                                color: appColors.blue,
                                flexShrink: 0,
                              }}
                            >
                              <FaDownload style={{ fontSize: 13 }} />
                            </span>
                          ) : (
                            <Badge tone="ink3">{t("noFile")}</Badge>
                          )}
                        </div>

                        {/* Footer estructurado: TIPO / SUBIDO EL */}
                        <div
                          style={{
                            marginTop: 12,
                            paddingTop: 12,
                            borderTop: `1px solid ${appColors.line2}`,
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>
                              {t("type")}
                            </div>
                            <Badge tone="blue">{labelDe(tipo)}</Badge>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>
                              {t("table.uploaded")}
                            </div>
                            <div style={{ ...appType.body, fontSize: 13.5, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
                              {subido || "—"}
                            </div>
                          </div>
                        </div>
                      </AppCard>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </AppScreen>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerIcon}><FaBuilding /></div>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>{t("title")}</h1>
            <p style={styles.subtitle}>{t("subtitle")}</p>
          </div>
        </div>
        <p style={{ textAlign: "center", color: "#6b7280", padding: "40px" }}>{t("loading")}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerIcon}><FaBuilding /></div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("title")}</h1>
          <p style={styles.subtitle}>{t("subtitle")}</p>
        </div>
      </div>

      {reglamentos.length > 0 && (
        <div style={styles.reglamentosSection}>
          <h2 style={styles.sectionTitle}>{t("sections.rules")}</h2>
          <div style={styles.reglamentosGrid}>
            {reglamentos.map((r) => (
              <a
                key={r.id}
                href={r.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.reglamentoCard}
              >
                <div style={styles.reglamentoIcon}>
                  <FaFileContract />
                </div>
                <div style={styles.reglamentoInfo}>
                  <div style={styles.reglamentoTitle}>{r.descKey ? t(`rules.${r.descKey}`) : r.description}</div>
                  {r.descKey && (
                    <div style={styles.reglamentoFileName}>{t(`ruleDesc.${r.descKey}`)}</div>
                  )}
                  {r.date && (
                    <div style={styles.reglamentoDate}>
                      {t("uploadedOn", { date: formatDate(r.date) })}
                    </div>
                  )}
                </div>
                <div style={styles.reglamentoDownload}>
                  <FaDownload />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {documentos.length === 0 && reglamentos.length === 0 ? (
        <EmptyStateComingSoon
          icon={<FaFolderOpen />}
          title={t("empty.title")}
          description={t("empty.description")}
        />
      ) : documentos.length === 0 ? null : (
        <>
        <h2 style={styles.sectionTitle}>{t("sections.documents")}</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{t("table.document")}</th>
              <th style={styles.th}>{t("table.type")}</th>
              <th style={styles.th}>{t("table.date")}</th>
              <th style={styles.th} aria-sort={sortDir === "desc" ? "descending" : "ascending"}>
                <button type="button" onClick={toggleSort} style={styles.sortHeader} title={t("sort.label")}>
                  {t("table.uploaded")}
                  {sortDir === "desc" ? <FaSortAmountDown /> : <FaSortAmountUp />}
                </button>
              </th>
              <th style={styles.th}>{t("table.action")}</th>
            </tr>
          </thead>
          <tbody>
            {documentos.map((doc) => {
              const tipo = tipoDe(doc);
              return (
              <tr key={doc.id} style={styles.row}>
                <td style={styles.td}>
                  <div style={styles.docName}>
                    <FaFileAlt style={styles.docIcon} />
                    <div style={{ minWidth: 0 }}>
                      <div>{doc.description || doc.descripcion || t("noDescription")}</div>
                      <div style={styles.docDesc}>{descDe(tipo)}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}>
                  <span style={styles.badge}>{labelDe(tipo)}</span>
                </td>
                <td style={styles.td}>{formatDate(doc.date || doc.fecha) || "—"}</td>
                <td style={styles.td}>{formatDate(uploadedAtOf(doc)) || "—"}</td>
                <td style={styles.td}>
                  {(doc.fileUrl || doc.archivo) ? (
                    <a href={doc.fileUrl || doc.archivo} target="_blank" rel="noopener noreferrer" style={styles.downloadButton}>
                      <FaDownload style={styles.downloadIcon} />
                      {t("view")}
                    </a>
                  ) : (
                    <span style={{ color: "#9ca3af", fontSize: "13px" }}>{t("noFile")}</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </>
      )}
    </div>
  );
};

const styles = {
  container: { padding: "24px", fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif", backgroundColor: "#f5f7fa", minHeight: "100vh" },
  header: { background: "white", borderRadius: "12px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "14px" },
  headerIcon: { width: "42px", height: "42px", borderRadius: "10px", backgroundColor: "#0080ff", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" },
  headerInfo: { flex: 1 },
  title: { fontSize: "20px", fontWeight: 400, margin: 0, color: "#0080ff" },
  subtitle: { fontSize: "13px", color: "#6b7280", margin: "2px 0 0 0", fontWeight: 400 },
  table: { width: "100%", borderCollapse: "collapse", backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
  th: { textAlign: "left", padding: "14px 16px", fontSize: "12px", fontWeight: 400, color: "#6b7280", backgroundColor: "#f8f9fa", textTransform: "uppercase", letterSpacing: "0.5px" },
  row: { borderTop: "1px solid #e5e7eb" },
  td: { padding: "14px 16px", fontSize: "14px", color: "#4b5563" },
  docName: { display: "flex", alignItems: "center", gap: "10px", fontWeight: 400, color: "#1a1a1a" },
  docIcon: { color: "#0080ff", fontSize: "16px", flexShrink: 0 },
  docDesc: { fontSize: "12px", color: "#6b7280", marginTop: "2px" },
  sortHeader: { background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: "inherit", textTransform: "inherit", letterSpacing: "inherit", display: "inline-flex", alignItems: "center", gap: "6px" },
  sortChip: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "999px", padding: "6px 12px", fontSize: "12px", color: "#4b5563", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", fontFamily: "inherit" },
  badge: { backgroundColor: "#e8f4ff", color: "#0080ff", padding: "4px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 400 },
  downloadButton: { backgroundColor: "#0080ff", color: "#fff", padding: "8px 14px", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: 400, display: "inline-flex", alignItems: "center", gap: "6px" },
  downloadIcon: { fontSize: "12px" },
  reglamentosSection: { marginBottom: "24px" },
  sectionTitle: { fontSize: "14px", fontWeight: 400, color: "#6b7280", margin: "0 0 12px 4px", textTransform: "uppercase", letterSpacing: "0.5px" },
  reglamentosGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" },
  reglamentoCard: { display: "flex", alignItems: "center", gap: "14px", padding: "16px", backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", textDecoration: "none", color: "inherit", transition: "transform 0.15s, box-shadow 0.15s" },
  reglamentoIcon: { width: "44px", height: "44px", borderRadius: "10px", backgroundColor: "#e8f4ff", color: "#0080ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 },
  reglamentoInfo: { flex: 1, minWidth: 0 },
  reglamentoTitle: { fontSize: "14px", color: "#1a1a1a", fontWeight: 400, marginBottom: "2px" },
  reglamentoFileName: { fontSize: "12px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  reglamentoDate: { fontSize: "11px", color: "#9ca3af", marginTop: "2px" },
  reglamentoDownload: { color: "#0080ff", fontSize: "14px", flexShrink: 0 },
};

export default DocumentacionEdificio;
